import React, { useState, useEffect, useRef } from 'react';
import { submitFeedback } from '../utils/api';


import LoadingSpinner from './LoadingSpinner';

const chatbotFBoptions = [
  'Too many steps', 'Unclear instructions', 'User Interface',
  'Features', 'Performance', 'Ease of Use',
  'Customer Support', 'Inconsistent use of graphics',
  'Not easy to undo / recover mistake', 'Visual bug',
  'Unable to show information at once'
];

function FeedbackModal({ isOpen, onClose }) {
  const [starRating, setStarRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [selectedFeedback, setSelectedFeedback] = useState([]);
  const [recommendation, setRecommendation] = useState(5);
  const [additionalComments, setAdditionalComments] = useState('');
  const [email, setEmail] = useState('');
  const [showThankYou, setShowThankYou] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const modalRef = useRef(null);

  const handleStarRating = (rating) => setStarRating(rating);
  const handleStarHover = (rating) => setHoverRating(rating);
  const handleStarLeave = () => setHoverRating(0);

  const handleFeedbackChange = (feedback) => {
    setSelectedFeedback((prevFeedback) =>
      prevFeedback.includes(feedback)
        ? prevFeedback.filter((item) => item !== feedback)
        : [...prevFeedback, feedback]
    );
  };
  // Handle form input changes
  const handleEmailChange = (e) => {
    const { name, value } = e.target;
    setEmail( value );
  };

  const [isSubmitting, setIsSubmitting] = useState(false); // Track submission status

  const handleSubmit = async () => {
    setIsSubmitting(true); // Start loading
    const feedbackData = {
      starRating,
      selectedFeedback,
      recommendation,
      additionalComments,
      email,
    };

    try {
      const response = await submitFeedback(feedbackData);

      console.log('Feedback submitted successfully:', response);
      setShowThankYou(true);
      setTimeout(() => {
        setShowThankYou(false);
        onClose();
      }, 3000); // 3 seconds
    } catch (error) {
      console.error('Error submitting feedback:', error.message);
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false); // End loading
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex justify-end bg-black bg-opacity-50 z-50">
      <div
        ref={modalRef}
        className="h-[90vh] w-full max-w-md bg-[#f4f4f4] text-[#53362e] shadow-lg overflow-y-auto rounded-lg mt-4 relative"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-xl font-bold text-[#53362e] hover:text-[#b09084]"
        >
          ×
        </button>

        <div className="p-6">
          {showThankYou ? (
            <div className="text-center mt-20">
              <h3 className="text-2xl font-bold mb-4 text-[#b09084]">Thank You!</h3>
              <p className="text-md">We appreciate your feedback and will use it to improve EasyErp.</p>
            </div>
          ) : (
            <>
              {/* Title */}
              <h3 className="text-xl font-bold mb-4 text-center text-[#b09084]">
                Do you like EasyErp?
              </h3>
              <input
                type="email"
                name="email"
                placeholder="Your Email Here"
                value={email}
                onChange={handleEmailChange}
                className="w-full p-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400"
                required
              />


              {/* Separator */}
              <div className="my-4 border-t border-gray-300"></div>

              {/* Star Rating */}
              <div className="section mb-4">
                <label className="block mb-2 font-semibold text-center">Rate your experience:</label>
                <div className="flex justify-center space-x-1 text-2xl">
                  {[...Array(5)].map((_, index) => {
                    const currentRating = index + 1;
                    return (
                      <span
                        key={index}
                        className={`cursor-pointer transition-colors ${
                          currentRating <= (hoverRating || starRating)
                            ? 'text-yellow-400'
                            : 'text-gray-300'
                        }`}
                        onMouseEnter={() => handleStarHover(currentRating)}
                        onMouseLeave={handleStarLeave}
                        onClick={() => handleStarRating(currentRating)}
                      >
                        ★
                      </span>
                    );
                  })}
                </div>
              </div>

              {/* Separator */}
              <div className="my-4 border-t border-gray-300"></div>

              {/* Improvement Multi-Select Dropdown */}
              <div className="section mb-4">
                <label className="block mb-2 font-semibold">What could be improved?</label>
                <div
                  className="cursor-pointer bg-white border border-gray-300 rounded-md p-2 flex justify-between items-center"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                >
                  <span>Select improvements</span>
                  <span className="text-sm">
                    {dropdownOpen ? '▲' : '▼'}
                  </span>
                </div>
                {dropdownOpen && (
                  <div className="bg-white border border-gray-300 ml-10 mr-10 rounded-md  max-h-40 overflow-y-auto p-2">
                    {chatbotFBoptions.map((feedback, index) => (
                      <label
                        key={index}
                        className="flex items-center space-x-1 cursor-pointer hover:bg-gray-100 rounded-md"
                      >
                        <input
                          type="checkbox"
                          checked={selectedFeedback.includes(feedback)}
                          onChange={() => handleFeedbackChange(feedback)}
                          className="accent-[#b09084]"
                        />
                        <span>{feedback}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>


              {/* Separator */}
              <div className="my-4 border-t border-gray-300"></div>
              {/* Recommendation Slider */}
              <div className="section mb-4">
                <label className="block mb-2 font-semibold">How likely are you to recommend us?</label>
                <div className="flex items-center space-x-2">
                  <span className="text-sm">1</span>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={recommendation}
                    onChange={(e) => setRecommendation(e.target.value)}
                    className="flex-grow accent-[#b09084]"
                  />
                  <span className="text-sm">10</span>
                </div>
                <div className="text-center text-sm mt-1 font-semibold">
                  {recommendation} / 10
                </div>
              </div>

              {/* Additional Comments */}
              <div className="section mb-4">
                <label className="block mb-2 font-semibold">Any additional comments?</label>
                <textarea
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-[#b09084]"
                  rows="3"
                  placeholder="Write your feedback here..."
                  value={additionalComments}
                  onChange={(e) => setAdditionalComments(e.target.value)}
                ></textarea>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                onClick={handleSubmit}
                className={`w-full font-bold py-3 rounded-md text-lg flex items-center justify-center gap-2 transition ${
                  isSubmitting
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-yellow-400 hover:bg-yellow-300'
                }`}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    Submitting <LoadingSpinner />
                  </>
                ) : (
                  'Submit'
                )}
              </button>

            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default FeedbackModal;
